import {
    externalFetch
} from '@js/actions/fetch';

import {pushError} from '@js/actions/errorManagement';

import AnalyticsService from '@js/modules/analyticsService';
import MapInterface from '@js/modules/maps/mapInterface';

const normalizeString = (string) => string ?
    string.normalize('NFD')
        .trim()
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s-_+@#$%^&*,;]/g, '')
        .toLowerCase()
    : string;

const MapRequest = (function () {
    const mapRequest = {
        // MapPlanner routing url
        mapPlannerDirectionUrl: 'https://routing.map-planner.com/route',

        // MapPlanner map-matching url (GraphHopper /match: recale un tracé main levée sur les routes)
        mapPlannerMatchUrl: 'https://routing.map-planner.com/match',

        // Mapbox routing url
        mapboxDirectionUrl: 'https://api.mapbox.com/directions/v5/mapbox/',

        // Mapbox url
        mapboxStaticMapUrl: 'https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/static',

        // Geocoder URL
        geocoderURL: 'https://geocoder.map-planner.com/'
    };

    const _normalizePlaceType = function (placeType) {
        switch (placeType) {
            case 'country':
                return 'place';
            case 'county':
                return 'place';
            case 'state':
                return 'place';
            case 'province':
                return 'place';
            case 'suburb':
                return 'place';
            case 'city':
                return 'place';
            case 'village':
                return 'place';
            case 'town':
                return 'place';
            case 'hamlet':
                return 'place';
            case 'locality':
                return 'place';
            case 'yes':
                return 'place';

            case 'peak':
                return 'peak';
            case 'volcano':
                return 'peak';

            case 'administrative':
                return 'address';
            case 'neighbourhood':
                return 'address';
            case 'house':
                return 'address';
            case 'apartments':
                return 'address';
            case 'residential':
                return 'address';
            case 'secondary':
                return 'address';
            case 'tertiary':
                return 'address';
            case 'university':
                return 'address';

            case 'station':
                return 'poi';
            case 'bus_stop':
                return 'poi';
            case 'subway_entrance':
                return 'poi';
            case 'orchard':
                return 'poi';
            case 'commercial':
                return 'poi';
            case 'government':
                return 'poi';
            case 'stream':
                return 'poi';
            case 'saddle':
                return 'poi';
            case 'parking':
                return 'poi';
            case 'service':
                return 'poi';
            case 'tree':
                return 'poi';
            case 'shelter':
                return 'poi';
            case 'platform':
                return 'poi';
            case 'viewpoint':
                return 'poi';
            case 'car_rental':
                return 'poi';
            case 'post_office':
                return 'poi';

            // case 'restaurant':
            //     return 'restaurant';

            case 'political':
                return false;

            default:
                return 'other';
        }
    };

    const _buildPlaceName = function (feature, {locationOnly = false} = {}) {
        const placeLocation = [
            feature.city,
            // feature.district || feature.city,
            feature.county || feature.postcode?.split(';')
                ?.first(),
            feature.countrycode === 'FR' ? null : feature.state,
            feature.country
        ].compact();

        if (locationOnly) {
            return placeLocation.join(', ');
        } else {
            let placeName = [feature.housenumber, feature.name || feature.street].compact().join(' ');

            if (placeLocation.length) {
                placeName += ` (${placeLocation.join(', ')})`;
            } else {
                placeName += ` (${feature.country})`;
            }

            return placeName;
        }
    };

    const _parsePhotonResponse = function (features) {
        if (features) {
            // Exclude useless data from photon response
            let filteredFeatures = features
                // .filter((feature) => ['place', 'address', 'peak', 'other'].includes(_normalizePlaceType(feature.properties.osm_value)))
                .filter((feature) => !['natural', 'census'].includes(feature.properties.osm_value));

            if (filteredFeatures.length < 3) {
                filteredFeatures = features;
            }

            return filteredFeatures
                .map((feature, index) => ({
                    type: feature.properties.type,
                    // osmId: feature.properties.osm_id,
                    osmType: feature.properties.osm_value,
                    placeType: _normalizePlaceType(feature.properties.osm_value),
                    name: [feature.properties.housenumber, feature.properties.name || feature.properties.street].compact().join(' '),
                    fullName: _buildPlaceName(feature.properties),
                    placeName: _buildPlaceName(feature.properties, {locationOnly: true}),
                    center: feature.geometry?.coordinates,
                    bbox: feature.properties.extent,
                    country: feature.properties.countrycode,
                    relevance: index
                }));
        } else {
            return [];
        }
    };

    const _buildPhotonGeocodingUrl = function (query, country, language = null, proximity = null, limit = 8) {
        // available layers:
        // house, street, locality, district, city, county, state, country, other (e.g. natural features)

        let geocodingUrl = MapRequest.geocoderURL + 'api/';

        geocodingUrl += `?q=${encodeURIComponent(query)}`;

        geocodingUrl += `&limit=${limit}`;

        geocodingUrl += '&layer=' + ['house', 'street', 'locality', 'district', 'city', 'state', 'other'].join('&layer=');

        // "tourism" and "amenity" tags are useful to find parkings
        geocodingUrl += '&osm_tag=!' + ['company', 'shop', 'emergency', 'healthcare', 'railway', 'office', 'boundary', 'club', 'craft', 'shop', 'highway', 'landuse', 'unclassified', 'man_made', 'leisure', 'bridge'].join('&osm_tag=!');

        if (proximity && proximity.lat !== 0 && proximity.lng !== 0) {
            let latitude = parseFloat(proximity.lat);
            let longitude = parseFloat(proximity.lng);

            // Ensure lat or long don't end with 0
            latitude = (latitude + 0.001).toFixed(3);
            longitude = (longitude + 0.001).toFixed(3);

            geocodingUrl += `&lat=${encodeURIComponent(latitude)}`;
            geocodingUrl += `&lon=${encodeURIComponent(longitude)}`;

            geocodingUrl += '&zoom=7';
        }

        if (language) {
            if (!['en', 'de', 'fr'].includes(language)) {
                language = 'default';
            }
            geocodingUrl += `&lang=${language}`;
        }

        return geocodingUrl;
    };

    // Build a minimal GPX 1.1 track from an array of {lat, lng} points.
    // GraphHopper /match expects a GPX body (application/gpx+xml).
    const _buildGpxTrack = function (pointsArray) {
        const precision = MapInterface.COORDINATES_PRECISION;
        const trackPoints = pointsArray
            .map((point) => `<trkpt lat="${point.lat.toFixed(precision)}" lon="${point.lng.toFixed(precision)}"/>`)
            .join('');

        return `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="MapPlanner" xmlns="http://www.topografix.com/GPX/1/1"><trk><trkseg>${trackPoints}</trkseg></trk></gpx>`;
    };

    const _mapRouteTypeToMapboxProfile = function (routeType) {
        switch (routeType) {
            case 'car':
            case 'car_fastest':
                return 'driving';
            case 'bike':
            case 'mtb':
                return 'cycling';
            default:
                return 'walking';
        }
    };

    // MapPlanner Graphhopper available route types:
    // - car: shortest path
    // - car_fastest: fastest path with turn costs
    // - foot: foot with elevation with a walking speed from 2 to 5km/h
    // - foot_basic: foot without elevation with a walking speed from 2 to 5km/h
    // - running: foot without elevation with a running speed to 9km/h
    // - hike: shortest hike with elevation
    // - hike_fastest: fastest hike with elevation
    // - bike: bike with elevation
    // - mtb: mountain bike with elevation
    mapRequest.getDirectionsAPIUrl = function (pointsArray, routingService, {
        routeType = 'hike',
        elevation = false
    } = {}, requestOptions = {}) {
        let url;

        let i;
        let encodedPoints = '';

        if (routingService === 'map_planner') {
            for (i = 0; i < pointsArray.length; i++) {
                if (encodedPoints.length > 0) {
                    encodedPoints += '&';
                }
                encodedPoints += 'point=' + encodeURIComponent(pointsArray[i].lat.toFixed(MapInterface.COORDINATES_PRECISION)) + ',' + encodeURIComponent(pointsArray[i].lng.toFixed(MapInterface.COORDINATES_PRECISION));
            }

            url = `${MapRequest.mapPlannerDirectionUrl}?${encodedPoints}&profile=${routeType}&type=json&elevation=${elevation}&instructions=false&points_encoded=true&details=road_class&details=surface`;
        } else {
            for (i = 0; i < pointsArray.length; i++) {
                if (encodedPoints.length > 0) {
                    encodedPoints += ';';
                }
                encodedPoints += pointsArray[i].lng.toFixed(MapInterface.COORDINATES_PRECISION) + ',' + pointsArray[i].lat.toFixed(MapInterface.COORDINATES_PRECISION);
            }

            const mapboxProfile = _mapRouteTypeToMapboxProfile(routeType);
            url = MapRequest.mapboxDirectionUrl + mapboxProfile + '/' + encodedPoints + '?access_token=' + window.settings.mapbox_key + '&overview=full&geometries=polyline' + (elevation ? '&annotations=duration,distance' : '');
        }

        return externalFetch(url, {}, requestOptions);
    };

    // Map-matching (Footpath-style): snap a freehand-drawn trace onto the road network.
    // Only the self-hosted GraphHopper (map_planner) exposes a /match endpoint here; for the
    // Mapbox region the caller falls back to routing through sampled waypoints.
    mapRequest.matchTrace = function (pointsArray, {
        routeType = 'hike',
        elevation = false,
        // Standard deviation (m) of the trace points. Higher values tell GraphHopper the input is
        // imprecise (finger drawing), which smooths the match and avoids short back-and-forth detours.
        gpsAccuracy = 50
    } = {}, {timeout} = {}) {
        const url = `${MapRequest.mapPlannerMatchUrl}?profile=${routeType}&type=json&elevation=${elevation}&instructions=false&points_encoded=true&gps_accuracy=${gpsAccuracy}&details=road_class&details=surface`;

        let signal;
        if (timeout && typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
            signal = AbortSignal.timeout(timeout);
        }

        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/gpx+xml',
                Accept: 'application/json'
            },
            body: _buildGpxTrack(pointsArray),
            signal
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json()
                        .then((data) => ({errors: data.message || response.statusText}))
                        .catch(() => ({errors: response.statusText}));
                }

                return response.json();
            })
            .catch((error) => {
                if (error.name === 'AbortError' || error.name === 'TimeoutError') {
                    return {abort: true};
                }

                return {errors: error.message};
            });
    };

    mapRequest.locationSearch = function (query, options = {}, requestOptions = {}) {
        const url = _buildPhotonGeocodingUrl(query, options.country, options.locale, options.proximity);
        const isFirefox = navigator.userAgent.indexOf('Firefox') !== -1;

        return externalFetch(url, {}, requestOptions)
            .then(function (data) {
                if (!data || data.errors) {
                    AnalyticsService.trackLocationSearchError(data?.errors);
                }

                if (!data || data.errors || data.abort) {
                    return [];
                }

                let features = _parsePhotonResponse(data.features);

                // Ensure all results are unique according to fullName to avoid duplication in search result
                features = features.uniqBy('fullName');

                if (options.country) {
                    features = data.features.filter((feature) => feature.country.toLowerCase() === options.country.toLowerCase());
                }

                features = features.filter((feature) => !!feature.name);

                return features
                    .compact()
                    .slice()
                    .sort((locationA, locationB) => {
                        if (options.sortByMatchingName) {
                            if (normalizeString(locationA.name).includes(normalizeString(query))) {
                                return isFirefox ? -1 : 1;
                            }
                        }

                        if (options.sortByLocaleCountry && options.locale) {
                            if (locationA.country?.toLowerCase() === options.locale.toLowerCase()) {
                                return isFirefox ? -1 : 1;
                            }
                        }

                        if (options.sortByPlaceType) {
                            if (locationA.type === 'county' && locationB.type === 'state') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'city' && locationB.type === 'county') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'city' && locationA.osmType === 'village' && locationB.type === 'city' && locationB.osmType === 'town') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'district' && locationB.type === 'city') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'locality' && locationB.type === 'district') {
                                return isFirefox ? -1 : 1;
                            }
                        }

                        // Sort by default by relevance
                        if (locationA.relevance < locationB.relevance) {
                            return isFirefox ? -1 : 1;
                        } else if (locationA.relevance > locationB.relevance) {
                            return isFirefox ? 1 : -1;
                        } else {
                            return 0;
                        }
                    });
            })
            .catch((error) => {
                pushError(error);
            });
    };

    return mapRequest;
})();

export default MapRequest;
